import { createRng } from '../core/rng';
import { drawBackground } from '../core/background';
import { anyPostEnabled, applyPostChain } from '../core/postfx/chain';
import type { PostChainConfig } from '../core/postfx/types';
import type { ParamValues } from '../core/params/types';
import type { TextField } from '../core/text/textField';
import type { TextSettings } from '../core/text/settings';
import { blendToComposite, TEXT_TECHNIQUE_ID, type Layer } from '../store/layer';
import { getTechnique, type RegisteredTechnique } from '../techniques/registry';
import type { Ctx2D, RenderTarget, TechniqueContext } from '../techniques/types';
import { drawText } from './fieldRender';

/**
 * Per-layer simulation state, kept between frames.
 *
 * A technique is re-initialised only when something it actually depends on changes —
 * its parameter values, its seed, or the text field itself. That is what lets an
 * iterative technique keep growing while you drag an unrelated slider on another layer.
 */
export interface LayerRuntime {
  signature: string;
  state: unknown;
  technique: RegisteredTechnique;
  /** Simulation steps already run. */
  steps: number;
}

export type RuntimeMap = Map<string, LayerRuntime>;

export function layerSignature(layer: Layer, fieldKey: string, width: number, height: number): string {
  return `${layer.techniqueId}|${layer.seed}|${width}x${height}|${fieldKey}|${JSON.stringify(layer.values)}`;
}

export interface CompositionInputs {
  field: TextField;
  fieldKey: string;
  textSettings: TextSettings;
  layers: readonly Layer[];
  /** Values of the background schema. */
  background: ParamValues;
  postFx: PostChainConfig;
  seed: number;
  width: number;
  height: number;
  /** 0–1 budget hint; the preview lowers it when the frame rate drops. */
  quality: number;
  scale: number;
}

export interface CompositionStats {
  drawMs: number;
  postMs: number;
  elements: number;
  activeLayers: number;
  animating: boolean;
}

export function contextFor(layer: Layer, inputs: CompositionInputs): TechniqueContext {
  return {
    field: inputs.field,
    width: inputs.width,
    height: inputs.height,
    rng: createRng(layer.seed),
    values: layer.values,
    scale: inputs.scale,
    quality: inputs.quality,
  };
}

/**
 * Brings every layer's runtime state up to date, re-initialising the ones whose inputs
 * changed and running the still-image step budget for iterative techniques.
 */
export function syncRuntimes(inputs: CompositionInputs, runtimes: RuntimeMap): void {
  const seen = new Set<string>();

  for (const layer of inputs.layers) {
    if (layer.techniqueId === TEXT_TECHNIQUE_ID) continue;
    const technique = getTechnique(layer.techniqueId);
    if (!technique) continue;

    seen.add(layer.id);
    const signature = layerSignature(layer, inputs.fieldKey, inputs.width, inputs.height);
    const existing = runtimes.get(layer.id);
    if (existing && existing.signature === signature) continue;

    // Only initialise here; the work happens in `step`, spread across frames.
    runtimes.set(layer.id, { signature, state: technique.init(contextFor(layer, inputs)), technique, steps: 0 });
  }

  for (const id of [...runtimes.keys()]) {
    if (!seen.has(id)) runtimes.delete(id);
  }
}

/**
 * Advances every unfinished layer by one frame. Returns true if anything changed,
 * which is what lets the render loop go idle once the composition has settled.
 */
export function stepRuntimes(inputs: CompositionInputs, runtimes: RuntimeMap, dt: number): boolean {
  let moved = false;
  for (const layer of inputs.layers) {
    const runtime = runtimes.get(layer.id);
    if (!runtime || !layer.visible) continue;
    if (runtime.technique.isComplete(runtime.state)) continue;
    runtime.state = runtime.technique.step(runtime.state, dt, contextFor(layer, inputs));
    runtime.steps += 1;
    moved = true;
  }
  return moved;
}

/**
 * Runs a layer to completion — used by exporters, which must not depend on how many
 * frames the preview happened to render. The cap protects against endlessly animated
 * techniques, which never report completion by design.
 */
export function runToCompletion(
  runtime: LayerRuntime,
  ctx: TechniqueContext,
  maxSteps = 20000,
): void {
  for (let i = 0; i < maxSteps && !runtime.technique.isComplete(runtime.state); i++) {
    runtime.state = runtime.technique.step(runtime.state, 1 / 60, ctx);
    runtime.steps += 1;
  }
}

export function drawComposition(ctx: Ctx2D, inputs: CompositionInputs, runtimes: RuntimeMap): CompositionStats {
  const t0 = performance.now();
  const { width, height } = inputs;

  drawBackground(ctx, inputs.background, width, height, inputs.seed);

  const target: RenderTarget = { ctx, width, height, scale: inputs.scale };
  let elements = 0;
  let activeLayers = 0;
  let animating = false;

  for (const layer of inputs.layers) {
    if (!layer.visible || layer.opacity <= 0) continue;

    ctx.save();
    ctx.globalAlpha = layer.opacity;
    ctx.globalCompositeOperation = blendToComposite(layer.blendMode);

    if (layer.techniqueId === TEXT_TECHNIQUE_ID) {
      const color = typeof layer.values.color === 'string' ? layer.values.color : '#f2f2f7';
      drawText(ctx, inputs.field, inputs.textSettings, color);
      activeLayers++;
    } else {
      const runtime = runtimes.get(layer.id);
      if (runtime) {
        runtime.technique.draw(runtime.state, target, contextFor(layer, inputs));
        elements += runtime.technique.elementCount?.(runtime.state) ?? 0;
        animating = animating || !runtime.technique.isComplete(runtime.state);
        activeLayers++;
      }
    }

    ctx.restore();
  }

  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';

  const drawMs = performance.now() - t0;
  const postMs = anyPostEnabled(inputs.postFx)
    ? applyPostChain(ctx, width, height, inputs.postFx, inputs.seed)
    : 0;

  return { drawMs, postMs, elements, activeLayers, animating };
}
